/**
 * Biome kit (WORKBOARD E5; owner, Oct 9: "more tree types, similar with rocks — relatively quotidian
 * but with a little intrigue / variation that's on brand", the brand a muted Astroneer × Ravenfield
 * mix): new tree species and rock types for biomes, as stand-ins against the style sheet.
 *
 * The look: faceted, rounded clumps and simple cones (Astroneer), low-poly and cheap to draw in
 * crowds (Ravenfield), matte and muted. Mostly ordinary — a broadleaf, a birch, a fir, a wind-bent
 * pine, a bottle tree, a dead snag; shelf slabs, an erratic boulder, basalt columns, a hoodoo — with
 * a little intrigue: the lantern fir's pale beads, the veined stone's faint seam.
 *
 * Model units match the game's flora and rocks (a tree ~0.3 tall at scale 1), so a species draws
 * at either size class: **giant** through today's path (TREE_DRAW_SCALE × the planet's prop scale —
 * a landmark), **normal** at NORMAL_TREE_SCALE, world-absolute (about six people at k 1/20 —
 * cover, in stands). Colours come from the biome (foliage, bark) or the planet (rock). Seeded:
 * peers draw the same tree. Presentation only; nothing in the sim reads these yet.
 *
 * Design reference: design_sheets/biomes.html
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import type { BiomeId, FloraType } from '../types';
import { TREE_TRUNK_SIZE, NORMAL_TREE_PERSONS, NORMAL_ROCK_PERSONS } from '../constants';
import { PERSON_BODY_HEIGHT } from '../core/scaleProfile';

export type TreeSpecies = 'puffElm' | 'paleBirch' | 'stackFir' | 'windPine' | 'bottleTree' | 'snag' | 'lanternFir';
export type RockKind = 'slab' | 'erratic' | 'columns' | 'hoodoo' | 'veined' | 'tor' | 'shard' | 'cobble';
export const TREE_SPECIES: TreeSpecies[] = ['puffElm', 'paleBirch', 'stackFir', 'windPine', 'bottleTree', 'snag', 'lanternFir'];
export const ROCK_KINDS: RockKind[] = ['slab', 'erratic', 'columns', 'hoodoo', 'veined', 'tor', 'shard', 'cobble'];

/** Model units → world units for the normal size class at k 1/20 (constants NORMAL_TREE_PERSONS × a person): a ~0.3-unit tree stands ~0.33 u, about six people. */
export const NORMAL_TREE_SCALE = NORMAL_TREE_PERSONS * PERSON_BODY_HEIGHT / 20;
/** …a kit rock ~0.2 tall stands ~0.09 u (a person and a half): cover, not a cliff. */
export const NORMAL_ROCK_SCALE = NORMAL_ROCK_PERSONS * PERSON_BODY_HEIGHT / 20;
/** …and ground flora (grass, reeds, lichen) waist-high around a person. */
export const NORMAL_GROUND_SCALE = 0.3;

/**
 * Trunks (model units at scale 1): radius and height — what a bullet, a walker and the eye stop at;
 * foliage never blocks (the game's rule, TREE_TRUNK_SIZE). The bottle tree is the thick one; firs
 * are short-trunked under their skirts; obstacles are upright cylinders, so the wind pine's leaning
 * trunk counts only its low part (the part that stays inside one).
 */
export const KIT_TRUNK: Record<TreeSpecies, { half: number; height: number }> = Object.fromEntries(
  ['puffElm', 'paleBirch', 'stackFir', 'windPine', 'bottleTree', 'snag', 'lanternFir'].map(sp => [sp, TREE_TRUNK_SIZE[sp]]),
) as Record<TreeSpecies, { half: number; height: number }>;   // the obstacle table's rows (constants TREE_TRUNK_SIZE)

export interface TreeColors { bark: string; leaf: string; leafDark: string; accent?: string }

/** Each biome's tree colours (core/biomes.ts holds the biome itself; colour is presentation). Muted, one family each. */
export const BIOME_COLORS: Record<BiomeId, TreeColors> = {
  broadleaf: { bark: '#5a4a3a', leaf: '#6f8a4c', leafDark: '#4e6638', accent: '#d6d2c4' },
  firStand: { bark: '#4a3c30', leaf: '#45604c', leafDark: '#324a3c' },
  heath: { bark: '#5c5048', leaf: '#66704a', leafDark: '#4c5638' },
  savanna: { bark: '#7a6450', leaf: '#8a8650', leafDark: '#6a683e' },
  ashBarrens: { bark: '#3e3632', leaf: '#5e5038', leafDark: '#463c2c' },
  lanternGrove: { bark: '#3a3a40', leaf: '#3e5660', leafDark: '#2c4048', accent: '#e6d89a' },
  // owner, Oct 10: the four from the same kit
  birchGlade: { bark: '#d8d2c4', leaf: '#a4bc6e', leafDark: '#7e9852', accent: '#f0ebe0' },   // light: white bark, bright leaf
  burn: { bark: '#2b2724', leaf: '#4a403a', leafDark: '#332c28' },                             // charred; the survivor fir keeps a little green in the dark
  scrub: { bark: '#8a7a62', leaf: '#8f9460', leafDark: '#6e7448' },                             // dusty olive
  hollowWood: { bark: '#4a4a58', leaf: '#6a7e8c', leafDark: '#4e5e6a', accent: '#bfe6ec' },     // sonora: slate and pale glass
};
/** Each biome's ground cover, drawn at a tree's foot (decoration: never an obstacle, never in state). */
export const BIOME_GROUND: Record<BiomeId, FloraType[]> = {
  broadleaf: ['velvetMatgrass'], firStand: ['velvetMatgrass'], heath: ['ghostReed'],
  savanna: ['thermocapLichen'], ashBarrens: ['thermocapLichen'], lanternGrove: ['velvetMatgrass', 'lanternBracket'],
  birchGlade: ['velvetMatgrass'], burn: ['thermocapLichen'], scrub: ['thermocapLichen'], hollowWood: ['ghostReed', 'lanternBracket'],
};
/** Ground cover's size beside a normal tree (its model units, relative to the tree's): reeds stay knee-high, mats and lichen ankle-to-shin. */
export const GROUND_TUFT_SCALE: Partial<Record<FloraType, number>> = { ghostReed: 0.1, velvetMatgrass: 0.2, thermocapLichen: 0.2, lanternBracket: 0.16 };
export interface KitModel { group: THREE.Group; sway: THREE.Object3D[] }

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string, glow = 0): THREE.MeshLambertMaterial {
  const key = `${color}:${glow}`;
  let m = matCache.get(key);
  if (!m) {
    m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true, ...(glow > 0 ? { emissive: new THREE.Color(color), emissiveIntensity: glow } : {}) }));
    matCache.set(key, m);
  }
  return m;
}
/** mulberry32: well mixed from the first draw (a bare xorshift's consecutive draws correlate, which skewed the picks). */
function rng(seed: number): () => number {
  let s = (seed * 2654435761) >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
/** Jitter a geometry's vertices by a hash of their position (shared corners agree: the mesh stays closed). */
function lumpy(geo: THREE.BufferGeometry, amount: number, offset: number): THREE.BufferGeometry {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const h = Math.sin(x * 127.1 + y * 311.7 + z * 74.7 + offset) * 43758.5453;
    const j = 1 + amount * ((h - Math.floor(h)) - 0.5) * 2;
    pos.setXYZ(i, x * j, y * j, z * j);
  }
  geo.computeVertexNormals();
  return geo;
}
function blob(parent: THREE.Object3D, r: number, color: string, x: number, y: number, z: number, sy = 1, detail = 1, off = 0): THREE.Mesh {
  const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(r, detail), 0.12, off), mat(color));
  m.position.set(x, y, z); m.scale.y = sy;
  parent.add(m);
  return m;
}
function trunk(parent: THREE.Object3D, rTop: number, rBot: number, h: number, color: string, x = 0, y = 0, z = 0, sides = 6): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(rTop, rBot, h, sides), mat(color));
  m.position.set(x, y + h / 2, z);
  parent.add(m);
  return m;
}
function cone(parent: THREE.Object3D, r: number, h: number, color: string, y: number, sides = 6): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.ConeGeometry(r, h, sides), mat(color));
  m.position.y = y + h / 2;
  parent.add(m);
  return m;
}
/** A limb from `a` toward direction (dx, dy, dz) of length `len`. */
function limb(parent: THREE.Object3D, r: number, len: number, color: string, ax: number, ay: number, az: number, dx: number, dy: number, dz: number): THREE.Mesh {
  const d = new THREE.Vector3(dx, dy, dz).normalize();
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.6, r, len, 5), mat(color));
  m.position.set(ax + d.x * len / 2, ay + d.y * len / 2, az + d.z * len / 2);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d);
  parent.add(m);
  return m;
}

// ---------------------------------------------------------------------------
// Trees (model units: ~0.25–0.4 tall at scale 1, base at y = 0)
// ---------------------------------------------------------------------------

function puffElm(g: THREE.Group, c: TreeColors, r: () => number, sway: THREE.Object3D[]): void {
  trunk(g, 0.012, 0.02, 0.13, c.bark);
  const crown = new THREE.Group(); crown.position.y = 0.13; g.add(crown); sway.push(crown);
  blob(crown, 0.085, c.leaf, 0, 0.07, 0, 0.9, 1, r() * 9);
  blob(crown, 0.06, c.leafDark, 0.06, 0.03, 0.02, 0.9, 1, r() * 9);
  blob(crown, 0.055, c.leaf, -0.05, 0.035, -0.03, 0.9, 1, r() * 9);
  blob(crown, 0.045, c.leafDark, 0.01, 0.14, 0.02, 0.9, 1, r() * 9);
}

function paleBirch(g: THREE.Group, c: TreeColors, r: () => number, sway: THREE.Object3D[]): void {
  trunk(g, 0.007, 0.012, 0.32, c.accent ?? '#d8d4c8');
  // the dark lenticels: short marks on the bark, not rings
  for (let i = 0; i < 7; i++) {
    const y = 0.04 + i * 0.038 + r() * 0.01, a = r() * Math.PI * 2, rad = 0.012 - 0.005 * (y / 0.32);
    const mark = new THREE.Mesh(new THREE.BoxGeometry(0.009, 0.0035, 0.004), mat('#4a4540'));
    mark.position.set(Math.cos(a) * rad, y, Math.sin(a) * rad); mark.rotation.y = -a + Math.PI / 2;
    g.add(mark);
  }
  const crown = new THREE.Group(); g.add(crown); sway.push(crown);
  const clumps: Array<[number, number, number, number]> = [[0.04, 0.2, 0.0, 0.042], [-0.035, 0.24, 0.02, 0.038], [0.02, 0.29, -0.03, 0.036], [-0.01, 0.33, 0.01, 0.03], [0.03, 0.15, 0.03, 0.03]];
  for (const [x, y, z, s] of clumps) {
    limb(crown, 0.004, Math.hypot(x, z) + 0.01, c.accent ?? '#d8d4c8', 0, y - 0.02, 0, x, 0.4, z);
    blob(crown, s, r() < 0.5 ? c.leaf : c.leafDark, x, y, z, 0.85, 0, r() * 9);
  }
}

function stackFir(g: THREE.Group, c: TreeColors, r: () => number, sway: THREE.Object3D[], beads = false): void {
  trunk(g, 0.01, 0.016, 0.08, c.bark);
  const crown = new THREE.Group(); g.add(crown); sway.push(crown);
  const tiers = 4;
  for (let i = 0; i < tiers; i++) {
    const rr = 0.1 * (1 - i * 0.2), h = 0.11 * (1 - i * 0.12), y = 0.05 + i * 0.065;
    const m = cone(crown, rr, h, i % 2 ? c.leafDark : c.leaf, y, 6);
    m.rotation.y = r() * Math.PI; m.rotation.z = (r() - 0.5) * 0.06;
    if (beads) {
      // the lantern fir: droplets hanging under every tier's edge — the one odd thing about it. 67.31 (the owner: "making
      // them actually glow … to test at night"): three a tier, larger and lit, so a grove reads at night (no light, no bloom)
      for (let k = 0; k < 3; k++) {
        const a = (k / 3) * Math.PI * 2 + r() * 2;
        const b = new THREE.Mesh(new THREE.IcosahedronGeometry(0.0075, 0), mat(c.accent ?? '#e6d89a', 1.1));
        b.scale.y = 1.7;
        b.position.set(Math.cos(a) * rr * 0.8, y - 0.008, Math.sin(a) * rr * 0.8);
        crown.add(b);
      }
    }
  }
}

function windPine(g: THREE.Group, c: TreeColors, r: () => number, sway: THREE.Object3D[]): void {
  // a trunk leaning with the wind, a flat umbrella crown off-centre
  const lean = 0.25 + r() * 0.15;
  const t = trunk(g, 0.008, 0.015, 0.2, c.bark);
  t.rotation.z = -lean; t.position.x = Math.sin(lean) * 0.1; t.position.y = Math.cos(lean) * 0.1;
  const top = new THREE.Group(); top.position.set(Math.sin(lean) * 0.2, Math.cos(lean) * 0.2, 0); g.add(top); sway.push(top);
  blob(top, 0.09, c.leafDark, 0.02, 0.01, 0, 0.32, 1, r() * 9);
  blob(top, 0.06, c.leaf, -0.04, 0.03, 0.02, 0.4, 1, r() * 9);
  limb(g, 0.004, 0.07, c.bark, Math.sin(lean) * 0.12, Math.cos(lean) * 0.12, 0, -0.6, 0.5, 0.2);
}

function bottleTree(g: THREE.Group, c: TreeColors, r: () => number, sway: THREE.Object3D[]): void {
  // a swollen trunk (the bottle), short limbs, small tufts
  const pts = [[0.02, 0], [0.045, 0.03], [0.05, 0.08], [0.04, 0.13], [0.018, 0.17], [0.014, 0.19]].map(([x, y]) => new THREE.Vector2(x, y));
  const body = new THREE.Mesh(new THREE.LatheGeometry(pts, 7), mat(c.bark));
  g.add(body);
  const crown = new THREE.Group(); crown.position.y = 0.19; g.add(crown); sway.push(crown);
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * Math.PI * 2 + r() * 0.6;
    const dx = Math.cos(a), dz = Math.sin(a);
    limb(crown, 0.005, 0.06, c.bark, 0, 0, 0, dx, 0.7, dz);
    blob(crown, 0.03, k % 2 ? c.leaf : c.leafDark, dx * 0.045, 0.05, dz * 0.045, 0.55, 0, r() * 9);
  }
}

function snag(g: THREE.Group, c: TreeColors, r: () => number): void {
  // a dead trunk: broken top, two or three bare limbs, no leaves (it still stops a bullet)
  const t = trunk(g, 0.008, 0.017, 0.26, c.bark, 0, 0, 0, 5);
  t.rotation.z = (r() - 0.5) * 0.08;
  const n = 2 + Math.floor(r() * 2);
  for (let k = 0; k < n; k++) {
    const a = r() * Math.PI * 2, y = 0.1 + r() * 0.12;
    limb(g, 0.005, 0.05 + r() * 0.04, c.bark, 0, y, 0, Math.cos(a), 0.5 + r() * 0.5, Math.sin(a));
  }
  const top = trunk(g, 0.004, 0.008, 0.03, c.bark, 0, 0.26, 0, 4);   // the broken stub
  top.rotation.z = 0.4;
}

/** Build a tree of `species` in a biome's colours, at model scale `scale` (0.8–1.2 variety), seeded. */
export function buildTree(species: TreeSpecies, colors: TreeColors, scale: number, seed: number): KitModel {
  const g = new THREE.Group();
  const r = rng(seed);
  const sway: THREE.Object3D[] = [];
  if (species === 'puffElm') puffElm(g, colors, r, sway);
  else if (species === 'paleBirch') paleBirch(g, colors, r, sway);
  else if (species === 'stackFir') stackFir(g, colors, r, sway);
  else if (species === 'lanternFir') stackFir(g, colors, r, sway, true);
  else if (species === 'windPine') windPine(g, colors, r, sway);
  else if (species === 'bottleTree') bottleTree(g, colors, r, sway);
  else snag(g, colors, r);
  g.scale.setScalar(scale);
  g.rotation.y = r() * Math.PI * 2;
  return { group: g, sway };
}

// ---------------------------------------------------------------------------
// Rocks (model units: ~0.12–0.35 tall at scale 1, base at y = 0; coloured by the planet's rock)
// ---------------------------------------------------------------------------

function shade(hex: string, k: number): string { return '#' + new THREE.Color(hex).multiplyScalar(k).getHexString(); }

/** Build a rock of `kind` in the planet's rock colour, seeded. `accent` colours the veined stone's seam. */
export function buildRock(kind: RockKind, color: string, scale: number, seed: number, accent = '#cfe8f0'): THREE.Group {
  const g = new THREE.Group();
  const r = rng(seed);
  if (kind === 'slab') {
    // shelves of sedimentary stone, each a little offset and a shade apart
    let y = 0;
    for (let i = 0; i < 3; i++) {
      const w = 0.32 - i * 0.07, h = 0.05 + r() * 0.02, d = 0.22 - i * 0.04;
      const m = new THREE.Mesh(lumpy(new THREE.BoxGeometry(w, h, d, 2, 1, 2), 0.06, r() * 9), mat(shade(color, 0.92 + i * 0.07)));
      m.position.set((r() - 0.5) * 0.04, y + h / 2, (r() - 0.5) * 0.03); m.rotation.y = (r() - 0.5) * 0.4;
      g.add(m); y += h * 0.92;
    }
  } else if (kind === 'erratic') {
    // one smooth boulder, dropped by ice: rounder and less jagged than the game's rocks
    const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.13, 2), 0.05, r() * 9), mat(shade(color, 1.08)));
    m.scale.set(1.15, 0.8, 0.95); m.position.y = 0.09; g.add(m);
  } else if (kind === 'columns') {
    // columnar basalt: hexagonal prisms, tallest in the middle
    const n = 7;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r() * 0.3, rad = i === 0 ? 0 : 0.055 + r() * 0.02;
      const h = (i === 0 ? 0.3 : 0.12 + r() * 0.14);
      const m = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.032, h, 6), mat(shade(color, 0.85 + r() * 0.2)));
      m.position.set(Math.cos(a) * rad, h / 2, Math.sin(a) * rad); m.rotation.y = r();
      g.add(m);
    }
  } else if (kind === 'tor') {
    // a granite tor: three big rounded blocks stacked and offset, the top one smallest (a mountain rock)
    let y = 0;
    for (let i = 0; i < 3; i++) {
      const rr = 0.15 - i * 0.033;
      const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(rr, 1), 0.08, r() * 9), mat(shade(color, 0.95 + i * 0.06)));
      m.scale.set(1.25, 0.68, 1.05); m.position.set((r() - 0.5) * 0.06, y + rr * 0.72, (r() - 0.5) * 0.05); m.rotation.y = r() * 0.8;
      g.add(m); y += rr * 1.3;
    }
  } else if (kind === 'shard') {
    // a standing shard: a tall angular slab tilted a little, one edge catching the light (a mountain rock)
    const m = new THREE.Mesh(lumpy(new THREE.BoxGeometry(0.1, 0.42, 0.22, 1, 3, 2), 0.1, r() * 9), mat(shade(color, 1.0)));
    m.position.set(0, 0.19, 0); m.rotation.set((r() - 0.5) * 0.3, r() * Math.PI, 0.18 + r() * 0.15);
    g.add(m);
    const foot = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.09, 1), 0.12, r() * 9), mat(shade(color, 0.85)));
    foot.scale.set(1.3, 0.5, 1.1); foot.position.set(0.04, 0.03, 0.02); g.add(foot);
  } else if (kind === 'cobble') {
    // a cobble cluster: four or five small rounded boulders nested together (a field rock)
    const n = 4 + Math.floor(r() * 2);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r() * 0.5, rad = i === 0 ? 0 : 0.075 + r() * 0.03, rr = 0.065 + r() * 0.03;
      const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(rr, 1), 0.1, r() * 9), mat(shade(color, 0.9 + r() * 0.2)));
      m.scale.set(1.1, 0.75, 1); m.position.set(Math.cos(a) * rad, rr * 0.7, Math.sin(a) * rad); m.rotation.y = r() * 3;
      g.add(m);
    }
  } else if (kind === 'hoodoo') {
    // a weathered pillar of stacked lumps with a darker cap stone
    let y = 0;
    for (let i = 0; i < 4; i++) {
      const rr = 0.06 - i * 0.008 + (r() - 0.5) * 0.01;
      const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(rr, 1), 0.1, r() * 9), mat(shade(color, 1 + (i % 2) * 0.08)));
      m.scale.y = 1.1; m.position.y = y + rr; g.add(m); y += rr * 1.7;
    }
    const cap = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.06, 1), 0.08, r() * 9), mat(shade(color, 0.7)));
    cap.scale.set(1.2, 0.45, 1.1); cap.position.y = y + 0.02; g.add(cap);
  } else {
    // a plain angular stone with one thin, faintly glowing seam across it — the intrigue
    const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.12, 0), 0.15, r() * 9), mat(shade(color, 1.05)));
    m.scale.set(1.1, 0.75, 1); m.position.y = 0.08; g.add(m);
    const seam = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.008, 0.012), mat(accent, 0.7));
    seam.position.set(0, 0.09, 0.075); seam.rotation.set(0.2, 0.15, 0.25); g.add(seam);
  }
  shadeRock(g);
  g.scale.setScalar(scale);
  g.rotation.y = r() * Math.PI * 2;
  return g;
}

const vmatCache = new Map<string, THREE.MeshLambertMaterial>();
/** The rock's material with vertex colours on (the shade multiplies the colour); cached per colour and glow like `mat`. */
function vmat(from: THREE.MeshLambertMaterial): THREE.MeshLambertMaterial {
  const key = `${from.color.getHexString()}:${from.emissiveIntensity}`;
  let m = vmatCache.get(key);
  if (!m) { m = markShared(from.clone()); m.vertexColors = true; vmatCache.set(key, m); }
  return m;
}
/**
 * Owner, Oct 10 ("improving rocks"): a rock reads as a rock when its top is lit and its base is in
 * the dirt — vertex colours shade each stone from 0.72 at the bottom to 1.12 at the top, with a
 * little per-face jitter so the facets differ. Zero runtime cost; the geometry stays one mesh.
 */
export function shadeRock(g: THREE.Object3D): void {
  g.traverse(o => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !(m.material as THREE.MeshLambertMaterial).isMeshLambertMaterial) return;
    const mat0 = m.material as THREE.MeshLambertMaterial;
    if (mat0.emissive.getHex() !== 0) return;                                // the veined stone's seam keeps its glow flat (Lambert's emissiveIntensity is 1 even when the emissive is black)
    const geo = m.geometry;
    geo.computeBoundingBox();
    const lo = geo.boundingBox!.min.y, hi = geo.boundingBox!.max.y, span = Math.max(1e-6, hi - lo);
    const pos = geo.attributes.position as THREE.BufferAttribute, col = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      const t = (pos.getY(i) - lo) / span;
      const face = Math.floor(i / 3), h = Math.sin(face * 12.9898 + 78.233) * 43758.5453, jitter = 0.96 + 0.08 * (h - Math.floor(h));
      const shade = (0.72 + 0.4 * t) * jitter;
      col[i * 3] = shade; col[i * 3 + 1] = shade; col[i * 3 + 2] = shade;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    m.material = vmat(mat0);
  });
}
